import { z } from "zod";
import type { CountryCode } from "libphonenumber-js";
/**
 * ⚠️ **不要加 `with { type: "json" }`**：这个名字在包的 exports 里对到的是
 *    `examples.mobile.json.js`（一支 JS 模块，不是纯 JSON），
 *    加了断言 Node 会直接以 `ERR_IMPORT_ATTRIBUTE_TYPE_INCOMPATIBLE` 拒绝加载，
 *    整个 API 起不来。（tw 那个项目踩过一次，别再加回去。）
 */
import examples from "libphonenumber-js/examples.mobile.json";
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js/mobile";

/**
 * ═══════════════════════════════════════════════════════════════
 *  手机号的国际化
 * ═══════════════════════════════════════════════════════════════
 *
 * 老板：「手机号调整为支持不同地区，可以参考隐形眼镜项目的会员基本资料里面的
 * 手机号，也是默认台湾，然后是中国，下面是其他地区手机号」。
 *
 * 🔴 **规则不自己写，吃 `libphonenumber-js`**（Google libphonenumber 的移植）。
 *    手写各国号段必错，而错的方式是「客人填的真号码被挡下来」；
 *    各国号段本来就会变（新开的、回收的），手写的那份只会越来越旧。
 *
 * 🔴 **用的是 `/mobile` 这份 metadata**，不是默认也不是 `/max`：
 *    它只认**手机**号，座机直接判不合法。这个平台的通知走短信，座机收不到。
 *
 * 🔴 **存进库的一律是 E.164**（`+8613700137000`）：`+` 加国码加号码，
 *    不留空格、不留破折号、不留前导 0。同一支号码有两种写法，
 *    就是找不到人、也是同一个人能注册两次的根源。
 *
 * 🔴 **默认地区是台湾，所以 `normalizePhone("13700137000")` 会算成
 *    `+88613700137000`（一个不合法的台湾号），不是 `+86...`。**
 *    这不是 bug，是"没有 `+` 就按默认地区解析"的必然结果 —— 光看数字
 *    没有任何办法知道 `13700137000` 是哪个地区的。
 *
 *    ⚠️ **所以登录不能只拿这个函数去查库。** 库里现有的账号全是
 *    `13700137000` 这种裸写法，而大陆用户登录时也习惯直接打这串数字。
 *    登录那边用的是**一组候选值**（原样 / 正规化 / `+86` / `+886`）去匹配，
 *    见 `auth/service.ts` 的 `phoneCandidates`。这条不守的话，
 *    上线当天所有老用户一起登不进来。
 */

/** 下拉里的一列。 */
export interface PhoneRegion {
  /** ISO 3166-1 alpha-2。旗子和 key 都用它。 */
  readonly code: string;
  /** 国码，不含 `+`。 */
  readonly dial: string;
  /** 中文名称。 */
  readonly name: string;
}

/**
 * 默认地区。
 *
 * 🔴 **台湾**（老板指定：「也是默认台湾，然后是中国」）。
 * ⚠️ 这个值同时是 `normalizePhone` 对**不带 `+` 的号码**的兜底解析地区。
 *    所以裸的 `13700137000` 会被当成台湾本地号 → `+88613700137000`。
 *    改这个常量会改变所有"没带国码"输入的归属，登录那边的候选值也要一起看。
 */
export const DEFAULT_PHONE_COUNTRY = "TW";

/**
 * 置顶的几个。**顺序是老板指定的**：台湾 → 中国 →（港澳与邻近市场）。
 * 其余两百多个按中文名排序，靠搜索找。
 */
const PINNED = ["TW", "CN", "HK", "MO", "JP", "KR", "SG", "MY", "US"] as const;

/**
 * `Intl.DisplayNames` 给的官方称呼有几个太长，下拉里会被截掉。
 * ⚠️ 只改**显示**，`code` 和国码完全不动。
 */
const NAME_OVERRIDE: Record<string, string> = {
  HK: "香港",
  MO: "澳门",
  US: "美国",
  GB: "英国",
  KR: "韩国",
  AE: "阿联酋",
  VA: "梵蒂冈",
};

const displayNames = new Intl.DisplayNames(["zh-Hans"], { type: "region" });

function regionName(code: string): string {
  const override = NAME_OVERRIDE[code];
  if (override !== undefined) return override;
  try {
    return displayNames.of(code) ?? code;
  } catch {
    /* 没有 ICU 数据的运行环境：退回代码，别让整页坏掉 */
    return code;
  }
}

/**
 * 全部国家／地区。
 * ⚠️ 这份是**算出来的**（libphonenumber 的清单 + `Intl` 的名称），
 *    不是手写的常量表 —— 所以包升级时它自己跟着更新。
 */
export const PHONE_REGIONS: readonly PhoneRegion[] = (() => {
  const rows: PhoneRegion[] = getCountries().map((code) => ({
    code,
    dial: getCountryCallingCode(code),
    name: regionName(code),
  }));
  const pinnedSet = new Set<string>(PINNED);
  const pinned = PINNED.map((code) => rows.find((r) => r.code === code)).filter(
    (r): r is PhoneRegion => r !== undefined,
  );
  const rest = rows
    .filter((r) => !pinnedSet.has(r.code))
    .sort((a, b) => a.name.localeCompare(b.name, "zh-Hans"));
  return [...pinned, ...rest];
})();

export function phoneRegionByCode(code: string): PhoneRegion | undefined {
  return PHONE_REGIONS.find((r) => r.code === code);
}

/** 这支号码属于哪个国家／地区。认不出来时 `undefined`。 */
export function phoneCountryOf(e164: string): PhoneRegion | undefined {
  const parsed = parsePhoneNumberFromString(e164);
  return parsed?.country === undefined ? undefined : phoneRegionByCode(parsed.country);
}

/**
 * 号码的**本地写法**（不含国码），给输入框回填用。
 * 例：`+8613700137000` → `13700137000`。
 */
export function localPartOf(e164: string): string {
  const parsed = parsePhoneNumberFromString(e164);
  if (parsed === undefined) return e164.replace(/^\+/, "");
  return parsed.formatNational().replace(/\D/g, "");
}

/** 这个地区的号码长什么样，放进 placeholder 和错误提示里。 */
export function phoneExample(code: string): string {
  try {
    const sample = getExampleNumber(code as CountryCode, examples);
    return sample === undefined ? "" : sample.formatNational();
  } catch {
    return "";
  }
}

/** 地区 + 本地号码 → E.164。**号码填一半时回空字符串**，别吐一个只有国码的东西。 */
export function toE164(countryCode: string, localRaw: string): string {
  const digits = localRaw.replace(/\D/g, "");
  if (digits === "") return "";
  const parsed = parsePhoneNumberFromString(digits, countryCode as CountryCode);
  if (parsed !== undefined) return parsed.number;
  /* 解析不出来（打到一半）也要回一个能继续校验的东西 */
  const region = phoneRegionByCode(countryCode);
  return `+${region?.dial ?? ""}${digits.replace(/^0+/, "")}`;
}

/**
 * 把用户打的东西整理成 E.164。
 *
 * 认得住这几种（都是实际会被打进来的）：
 *   `+8613700137000` · `8613700137000` · `13700137000` · `137-0013-7000` · `+886 912 345 678`
 *
 * 🔴 **没有 `+` 时要先试「这是不是已经带国码的号码」**（`8613700137000`、
 *    `886912345678`）—— 直接按默认地区解析的话，中国号会被接成
 *    `+886 13700137000` 这种怪东西，长度也不对，只会在存档时被打回来。
 *    只有**整串真的是一支合法号码**才算命中，否则才按默认地区当本地号解析。
 *
 * 🔴 **裸的 11 位中国号在这里会变成台湾号**：`13700137000` 整串按国际解析
 *    不合法（`+13700137000` 不是任何国家的号），于是落到"按默认地区解析"
 *    这一条 → `+88613700137000`。**这是设计使然，不是 bug** ——
 *    光看数字没有任何办法知道它是哪个地区的。
 *    ⚠️ 库里存量账号存的是裸的 `13700137000`，所以**登录不能只用这一个值查库**，
 *    要用一组候选值（见 `auth/service.ts` 的 `phoneCandidates`）。
 *    `phone.test.ts` 把这个行为钉死了 —— 它是登录那套候选值的前提。
 *
 * ⚠️ 解析不出来时**原样返回**（去掉空白），交给 `phoneProblem` 去说哪儿不对 ——
 *    在这里硬凑一个格式只会让错误提示指到错的地方。
 */
export function normalizePhone(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") return "";

  if (trimmed.startsWith("+")) {
    return parsePhoneNumberFromString(trimmed)?.number ?? trimmed.replace(/\s/g, "");
  }

  /**
   * 🔴 **先按"这是某个地区的本地号码"试，再按"这串数字自带国码"试。**
   *
   *    顺序反过来会出真事故：中国大陆手机号是 **11 位、以 1 开头**
   *    （`13163149815`），而它同时也是一个**合法的美国号码**
   *    （`+1` 316-314-9815）—— 先试 `+数字` 的话，
   *    北美号段会把每一个裸写的大陆号都吃掉，存进库里变成 `+13163149815`。
   *    真机上就是这么发生的：后台代开一个号，存出来是美国号。
   *
   *    ⚠️ 参考项目（台湾站）没暴露这个问题，因为台湾手机号是 `09xxxxxxxx`，
   *       `+09…` 不合法，那条捷径永远命中不了。抄过来时要连这一点一起改。
   *
   *    ⚠️ `NATIONAL_GUESS` 里**只放这个站真实会遇到的地区**。放一长串的话，
   *       几乎任意一串数字都能在某个国家里"合法"，等于又回到瞎猜。
   */
  const NATIONAL_GUESS = [DEFAULT_PHONE_COUNTRY, "CN", "TW", "HK"];
  for (const region of NATIONAL_GUESS) {
    const parsed = parsePhoneNumberFromString(trimmed, region as CountryCode);
    if (parsed?.isValid() === true) return parsed.number;
  }

  /** 到这儿才认为"这串数字自带国码"（`8613812345678`、`886912345678`）。 */
  const digits = trimmed.replace(/\D/g, "");
  const asInternational = parsePhoneNumberFromString(`+${digits}`);
  if (asInternational?.isValid() === true) return asInternational.number;

  /**
   * ⚠️ 都认不出来就**原样返回**（只去掉空白），交给 `phoneProblem` 去说哪儿不对 ——
   *    在这里硬凑一个格式，只会让错误提示指到错的地方。
   */
  return trimmed.replace(/\s/g, "");
}

/**
 * 这串 E.164 合法吗，不合法时回一句**说得出哪儿不对**的话。
 *
 * ⚠️ 「不是手机」和「格式不对」要分开讲：很多人会填座机，
 *    只说「格式不正确」他会一直重打同一个座机号。
 */
export function phoneProblem(e164: string): string | null {
  if (e164.trim() === "") return "请输入手机号";
  const parsed = parsePhoneNumberFromString(e164);
  if (parsed === undefined) return "看不懂这个号码，请确认地区和号码";

  if (parsed.isValid()) return null;

  const region = parsed.country === undefined ? undefined : phoneRegionByCode(parsed.country);
  if (region === undefined) return "号码不正确，请确认地区和号码";

  const example = phoneExample(region.code);
  return example === ""
    ? `这不像${region.name}的手机号，请再确认一次`
    : `这不像${region.name}的手机号（该地区的格式像这样：${example}）`;
}

/**
 * 会员手机号。**存进库的是 E.164。**
 *
 * ⚠️ 先正规化再校验，顺序不能颠倒：用户实际会打的是 `137-0013-7000`、
 *    `+886 912 345 678`、`8613700137000` 这几种，先挡格式的话，
 *    写的明明是同一支号码却有几种会被拒，而用户看不出自己哪儿错了。
 */
export const memberPhoneSchema = z
  .string()
  .trim()
  .min(1, "请输入手机号")
  .transform(normalizePhone)
  .superRefine((value, ctx) => {
    const problem = phoneProblem(value);
    if (problem !== null) ctx.addIssue({ code: "custom", message: problem });
  });

/**
 * 显示用：`+886 912 345 678`。
 * ⚠️ 用**国际格式**不用本地格式：客人可能填的是别的地区，
 *    印成本地写法会让两个地区的号码长得一样而分不出来。
 */
export function formatPhone(e164: string): string {
  if (e164 === "") return "";
  const parsed = parsePhoneNumberFromString(e164);
  return parsed === undefined ? e164 : parsed.formatInternational();
}
